// Route-access allow-list with dependency validation.
//
// DESIGN PATTERN:
//   menuData → Master Pages → Detail Pages → Edit/Add Pages
//
// RULE: A URL under /main/* is only reachable if it is:
//   (a) A "master" page link from menuData.jsx (visible in sidebar)
//       Example: /main/v2/ClentDetails (master list page)
//
//   (b) A dependent "detail" or "popup" page opened by clicking a row in the master page
//       (NOT in menuData because it's context-dependent on which row was clicked)
//       Example: /main/editCompany/123 (edit form, only accessible after opening master)
//
//   (c) A sub-dependent "edit/add" page opened from the detail page
//       Example: /main/newCompany (add form, only for adding new records)
//
//   (d) Utility pages explicitly exempted (accessible regardless of context)
//       Example: /main/change-password
//
// BLOCKED: Direct URLs to routes not in the allow-list
//   Example: /main/admin/hack → Redirects to /main/home with warning
//
// KEY: Routes are organized by dependency chain:
//   - Master: Entry point (always safe to access)
//   - Detail: Depends on Master (safe, used by master workflows)
//   - Edit/Add: Depends on Detail (safe, used by detail workflows)
//
// Matching is by prefix (e.g. "/editCompany" allows "/editCompany/123"),
// so only the static portion of each route is needed, not full param patterns.

const ALLOWED_PREFIXES = [
    // Dashboard
    "/home",

    // ─── MASTER PAGES (visible in menuData) ───────────────────────────────────────
    "/company",
    "/v2/ClentDetails",
    "/v2/productDetail",
    "/BankCashPosting",
    "/AddOnCharges",
    "/QuatationDetails",
    "/v2/orderDetail-stepper",
    "/proforma-invoice",
    "/order-dispatch",
    "/v2/sale-invoice",
    "/CreditNote-Details",
    "/receivable-details",
    "/payable-details",
    "/VoucherDetails",

    // ─── DETAIL/LIST PAGES (popup components, accessed from master) ───────────────
    // ✓ These open from clicking rows in master pages
    // ✓ NOT visible in menuData, but required for master page workflows
    // ✓ Dependency: Master → Detail
    "/CandidateDetails",           // Dependency: CandidateMaster
    "/consignee-details",          // Dependency: (master page for consignees)
    "/currencyDetail",             // Dependency: (master page for currencies)
    "/v2/currencyDetail",
    "/godownDetail",               // Dependency: (master page for godowns)
    "/hsnDetail",                  // Dependency: (master page for HSN)
    "/unitDetail",                 // Dependency: (master page for units)
    "/proGroupDetail",             // Dependency: (master page for product groups)
    "/v2/proGroupDetail",
    "/AccGrpDetails",              // Dependency: AccGrpMaster

    // ─── EDIT/ADD PAGES (popup components, accessed from detail) ───────────────────
    // ✓ These open from detail pages (when user clicks "Add" or "Edit")
    // ✓ Dependency: Master → Detail → Edit
    "/newCompany",                 // Dependency: company detail
    "/editCompany",                // Dependency: company detail
    "/newClient",                  // Dependency: client detail
    "/insertClient",               // Dependency: client detail
    "/editClient",                 // Dependency: client detail
    "/insertProduct",              // Dependency: product detail
    "/editProductMaster",          // Dependency: product detail
    "/productMaster",              // Dependency: product detail
    "/CandidateMaster",            // Dependency: CandidateDetails
    "/editCandidate",              // Dependency: CandidateDetails
    "/insertCandidates",           // Dependency: CandidateDetails
    "/consignee-master",           // Dependency: consignee-details
    "/edit-consignee",             // Dependency: consignee-details
    "/currencyMaster",             // Dependency: currencyDetail
    "/editCurrencyMaster",         // Dependency: currencyDetail
    "/godownMaster",               // Dependency: godownDetail
    "/editGodownMaster",           // Dependency: godownDetail
    "/hsnMaster",                  // Dependency: hsnDetail
    "/editHsnMaster",              // Dependency: hsnDetail
    "/unitMaster",                 // Dependency: unitDetail
    "/editUnitMaster",             // Dependency: unitDetail
    "/proGroupMaster",             // Dependency: proGroupDetail
    "/editProGroupMaster",         // Dependency: proGroupDetail
    "/AccGrpMaster",               // Dependency: AccGrpDetails
    "/editAccGrp",                 // Dependency: AccGrpDetails

    // Transaction — Quotation
    "/QuatationDetails", "/v3/quatationMaster", "/v3/editquatationMaster", "/v3/quatationDetail",

    // Transaction — Sales Order
    "/v2/orderDetail-stepper", "/v3/orderMaster-stepper", "/v3/orderMaster-stepper-ref", "/v3/orderMaster-edit",

    // Transaction — Proforma Invoice
    "/proforma-invoice", "/ProformaInvoiceMaster-stepper", "/ProformaInvoiceMaster-stepper-proforma", "/ProformaInvoiceMaster-edit",

    // Transaction — Sale Challan
    "/order-dispatch", "/v3/challanMaster-stepper", "/v3/challanMaster-stepper-ref", "/v3/challanMaster-edit",

    // Transaction — Sale Invoice (+ Prev FY, shares the same stepper)
    "/v2/sale-invoice", "/v2/sale-invoice-prev-fy",
    "/v3/InvoiceMaster-stepper", "/v3/InvoiceMaster-stepper-ref", "/v3/InvoiceMaster-edit",

    // Transaction — Credit Note
    "/CreditNote-Details", "/CreditNote-Master-stepper", "/CreditNote-Master-stepper-ref", "/CreditNote-Master-edit",

    // Transaction — Purchase Order
    "/purchase-orderDetails", "/Purchase-OrderMaster-stepper", "/Purchase-OrderMaster-stepper-ref", "/Purchase-OrderMaster-edit",

    // Transaction — Purchase Challan
    "/Purchase-ChallanDetails", "/Purchase-ChallanMaster-stepper", "/Purchase-ChallanMaster-stepper-ref", "/Purchase-ChallanMaster-edit",

    // Transaction — Purchase Invoice (+ Prev FY, shares the same stepper as Sale Invoice Prev FY)
    "/Purchase-InvoiceDetails", "/Purchase-InvoiceMaster-stepper", "/Purchase-InvoiceMaster-stepper-ref", "/Purchase-InvoiceMaster-edit",
    "/CreditExpense-Details", "/CreditExpense-Master-stepper", "/CreditExpense-Master-edit",
    "/v2/purchase-invoice-prev-fy",

    // Transaction — Debit Note
    "/DebitNote-Details", "/DebitNote-Master-stepper", "/DebitNote-Master-stepper-ref", "/DebitNote-Master-edit",

    // Transaction — Production (Store)
    "/store-details", "/store-master", "/store-master-edit",

    // Transaction — Receivable
    "/receivable-details", "/receivable-v2", "/receivable-edit",

    // Transaction — Payable
    "/payable-details", "/payable-v2", "/payable-edit",

    // Transaction — Vouchers
    "/VoucherDetails", "/Vouchers", "/voucher-edit", "/voucherDetails",

    // Reports (all render the same AccountReports component, different filters)
    "/account-report", "/gst-reports", "/OSandOD-bills-report",
    "/ledger-report", "/stock-report", "/mis-report", "/mis-purchase-report",

    // Settings
    "/PrintingUtilities", "/user-define-columns", "/configure", "/pricelist",

    // User Management
    "/v3/userdetails", "/userdetails", "/CandidateDetails", "/CandidateMaster",
    "/usermaster",                 // Dependency: userdetails / v3/userdetails (edit/add user)

    // Always-open utility pages (not in menuData, needed regardless)
    "/change-password",
];

/**
 * True if pathname (relative to /main, e.g. "/company" or "/editCompany/123")
 * is reachable — either it or one of its known prefixes is allow-listed.
 */
export function isRouteAllowed(pathname) {
    const normalized = pathname.replace(/\/+$/, "") || "/home";
    return ALLOWED_PREFIXES.some(
        (prefix) => normalized === prefix || normalized.startsWith(prefix + "/")
    );
}

export default ALLOWED_PREFIXES;
